import { Component, Input, OnChanges, SimpleChanges, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as QRCode from 'qrcode';
import { ConfigService } from '@servicios/config.service';

@Component({
  selector: 'app-item-label',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div id="printable-label" [ngClass]="['label-container', container ? 'standard-item' : (item?.type === 'TEST_ITEM' ? 'test-item' : 'standard-item')]" *ngIf="item || container">
      <!-- CONTAINER / FRASCO ANALÍTICO LAYOUT (50mm x 25mm) -->
      @if (container) {
        <div class="label-content standard-content">
          <div class="label-header">
            <div class="item-name">{{ item?.name || 'Insumo de Laboratorio' }}</div>
            <div class="item-code" style="font-size: 7.5pt; font-weight: bold; color: #000;">{{ container.container_code }}</div>
          </div>
          
          <div class="label-body">
            <div class="qr-container">
              <img [src]="qrCodeUrl()" *ngIf="qrCodeUrl()" alt="QR Code">
            </div>
            <div class="details">
              <div class="detail-row"><strong>Lote Fab:</strong> {{ lot?.lot_number || item?.lot_number || '-' }}</div>
              <div class="detail-row" *ngIf="lot?.internal_lot_code"><strong>Ctrl Int:</strong> {{ lot.internal_lot_code }}</div>
              <div class="detail-row" *ngIf="container.potency"><strong>Título:</strong> {{ container.potency }}</div>
              <div class="detail-row"><strong>Apertura:</strong> {{ container.opened_at ? (container.opened_at | date:'dd/MM/yy') : 'Sellado' }}</div>
              <div class="detail-row"><strong>Venc. Uso:</strong> {{ (container.in_use_expiration_date ? (container.in_use_expiration_date | date:'dd/MM/yy') : ((lot?.expiration_date || item?.expiration_date) | date:'dd/MM/yy')) || 'S/V' }}</div>
              <div class="detail-row"><strong>Cont:</strong> {{ container.remaining_content ?? container.initial_content }} {{ container.content_unit }}</div>
            </div>
          </div>
          
          <div class="label-footer">
            <span>FRASCO ANALÍTICO — CONTROL LIMS</span>
          </div>
        </div>
      } @else if (item.type === 'TEST_ITEM') {

        <div class="label-content test-content">
          <div class="test-grid">
            
            <!-- Left Info Area (2/3 Width) -->
            <div class="info-area">
              <div class="fields-container">
                <!-- List of all active ingredients -->
                <div class="field" *ngFor="let comp of compositionsList(); trackBy: trackByComp">
                  <span class="label">I.A.:</span>
                  <span class="value uppercase">{{ comp.name }} - {{ comp.conc }} {{ comp.unitLabel }}</span>
                </div>
                
                <div class="field" *ngIf="formulationCode">
                  <span class="label">Formulado:</span>
                  <span class="value uppercase">{{ formulationCode }}</span>
                </div>
                
                <div class="field" *ngIf="nc && nc !== 'NA'">
                  <span class="label">N.C.:</span>
                  <span class="value">{{ nc }}</span>
                </div>
                
                <!-- Combined Quantity and Expiration -->
                <div class="field-row">
                  <div class="field half">
                    <span class="label">Cant:</span>
                    <span class="value">{{ cant }}</span>
                  </div>
                  <div class="field half">
                    <span class="label">Venc:</span>
                    <span class="value">{{ venc }}</span>
                  </div>
                </div>
                
                <div class="field">
                  <span class="label">Prov:</span>
                  <span class="value">{{ prov }}</span>
                </div>
                
                <!-- Combined Storage Location and Lot -->
                <div class="field-row">
                  <div class="field half">
                    <span class="label">Alm:</span>
                    <span class="value">{{ alm }}</span>
                  </div>
                  <div class="field half">
                    <span class="label">Lote:</span>
                    <span class="value">{{ lote }}</span>
                  </div>
                </div>
                
                <div class="field">
                  <span class="label">Descarte:</span>
                  <span class="value">{{ descarte }}</span>
                </div>
                
                <div class="field" *ngIf="toxicologyRef">
                  <span class="label">Estudio:</span>
                  <span class="value font-bold">{{ toxicologyRef }}</span>
                </div>
              </div>

              <!-- Centered barcode/partition code at the bottom of left area -->
              <div class="code-display">
                {{ code }}
              </div>
            </div>

            <!-- Right Assets Area (1/3 Width) -->
            <div class="assets-area">
              <div class="logo-container">
                <img [src]="customerLogo" alt="Logo">
              </div>
              <div class="qr-container-test">
                <img [src]="qrCodeUrl()" *ngIf="qrCodeUrl()" alt="QR Code">
              </div>
            </div>

          </div>
        </div>
      } @else {
        <!-- STANDARD ITEM LAYOUT (50mm x 25mm) -->
        <div class="label-content standard-content">
          <div class="label-header">
            <div class="item-name">{{ item.name }}</div>
            <div class="item-code">{{ item.internal_code }}</div>
          </div>
          
          <div class="label-body">
            <div class="qr-container">
              <img [src]="qrCodeUrl()" *ngIf="qrCodeUrl()" alt="QR Code">
            </div>
            <div class="details">
              <div class="detail-row"><strong>Marca:</strong> {{ item.brand?.name || 'N/A' }}</div>
              <div class="detail-row"><strong>Lote:</strong> {{ item.lot_number || '---' }}</div>
              <div class="detail-row"><strong>Vence:</strong> {{ (item.expiration_date | date:'dd/MM/yy') || 'N/A' }}</div>
            </div>
          </div>
          
          <div class="label-footer">
            <span>LIMS INVENTORY SYSTEM</span>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: none; /* Hidden on screen */
    }

    @media print {
      @page {
        size: 80mm 25mm landscape;
        margin: 0;
      }

      :host {
        display: block !important;
      }

      .label-container {
        display: block !important;
        margin: 0;
        box-sizing: border-box;
        overflow: hidden;
        background: white;
        color: black;
      }

      /* Test Item dimensions */
      .label-container.test-item {
        width: 80mm;
        height: 25mm;
        padding: 0.6mm 1mm;
        font-family: Arial, Helvetica, sans-serif;
      }

      /* Standard Item dimensions */
      .label-container.standard-item {
        width: 50mm;
        height: 25mm;
        padding: 1mm;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      }

      .label-content {
        display: flex;
        flex-direction: column;
        height: 100%;
        box-sizing: border-box;
      }

      .test-content {
        height: 100%;
        border: 0.3mm solid #000;
        box-sizing: border-box;
        padding: 0.5mm 0.8mm;
      }

      .standard-content {
        border: 0.2mm solid #000;
        padding: 1mm;
      }

      .test-grid {
        display: flex;
        width: 100%;
        height: 100%;
        justify-content: space-between;
        align-items: stretch;
      }

      /* Left Area (2/3 width = 67%) */
      .info-area {
        width: 67%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        overflow: hidden;
      }

      .fields-container {
        display: flex;
        flex-direction: column;
        gap: 0.1mm;
      }

      .field-row {
        display: flex;
        width: 100%;
        justify-content: space-between;
        gap: 2mm;
      }

      .field {
        font-size: 5.6pt;
        line-height: 1.05;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0 !important;
        padding: 0 !important;
      }

      .field.half {
        width: 48%;
      }

      .label {
        font-weight: bold;
        margin-right: 0.4mm;
      }

      .value {
        font-weight: normal;
      }

      .uppercase {
        text-transform: uppercase;
      }

      .font-bold {
        font-weight: bold;
      }

      /* Main Code centered bottom left area */
      .code-display {
        font-size: 11pt;
        font-weight: bold;
        line-height: 1;
        text-align: center;
        width: 100%;
        margin-bottom: 0.2mm;
        white-space: nowrap;
        margin-top: 0.2mm;
      }

      /* Right Area (1/3 width = 31%) */
      .assets-area {
        width: 31%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        height: 100%;
        border-left: 0.2mm solid #ddd;
        padding-left: 1mm;
        box-sizing: border-box;
      }

      .logo-container {
        width: 100%;
        height: 8mm;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        padding-bottom: 0.8mm;
      }

      .logo-container img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        display: block;
      }

      .qr-container-test {
        width: 10.5mm;
        height: 10.5mm;
        display: block;
      }

      .qr-container-test img {
        width: 100%;
        height: 100%;
        display: block;
      }

      /* Styles for Standard Content */
      .label-header {
        border-bottom: 0.1mm solid #eee;
        margin-bottom: 1mm;
      }

      .item-name {
        font-size: 8pt;
        font-weight: bold;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .item-code {
        font-size: 6pt;
        color: #333;
      }

      .label-body {
        display: flex;
        gap: 2mm;
        flex: 1;
      }

      .qr-container {
        width: 12mm;
        height: 12mm;
      }

      .qr-container img {
        width: 100%;
        height: 100%;
      }

      .details {
        font-size: 5pt;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .detail-row {
        white-space: nowrap;
      }

      .label-footer {
        font-size: 4pt;
        text-align: center;
        color: #888;
        margin-top: auto;
      }
    }
  `]
})
export class ItemLabelComponent implements OnChanges {
  @Input() item: any;
  @Input() partition: any;
  @Input() container: any;
  @Input() lot: any;
  @Input() suppliers: any[] = [];
  @Input() locations: any[] = [];
  @Input() units: any[] = [];

  private configService = inject(ConfigService);
  qrCodeUrl = signal<string | null>(null);
  compositionsList = signal<any[]>([]);

  trackByComp(index: number, item: any): string {
    return (item?.name || '') + '_' + (item?.conc || '') + '_' + index;
  }

  get customerLogo(): string {
    const customer = this.configService.appSettings?.general?.customer?.toLowerCase();
    if (customer === 'genbra') {
      return 'assets/img/static/logo_genbra.png';
    }
    return 'assets/img/static/biworklims.png';
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['item'] || changes['partition'] || changes['units'] || changes['container'] || changes['lot']) {
      this.updateCompositions();
      await this.generateQR();
    }
  }

  private updateCompositions() {
    if (!this.item?.composition?.length) {
      this.compositionsList.set([]);
      return;
    }
    const comps = this.item.composition.map((comp: any) => {
      const name = comp.analite?.label || comp.analite?.name || 'N/A';
      const conc = comp.concentration !== undefined && comp.concentration !== null ? comp.concentration : '';

      const uId = comp.unit_id || comp.unitId;
      let unitLabel = '';
      if (uId) {
        const found = this.units.find((u: any) => u.value === uId || u.id === uId);
        if (found) {
          unitLabel = found.label || found.name || '';
        }
      }
      if (!unitLabel && comp.unit) {
        unitLabel = comp.unit.label || comp.unit.name || comp.unit.id || '';
      }

      return {
        name,
        conc,
        unitLabel
      };
    });
    this.compositionsList.set(comps);
  }

  async generateQR() {
    if (!this.item && !this.container) return;
    try {
      let data = '';
      if (this.container) {
        data = this.container.container_code;
      } else if (this.partition) {
        // Point partitions directly to the mass balance view
        data = `${window.location.origin}/laboratory/mass-balance/${this.item.id}`;
      } else {
        // Point main item directly to the item view
        data = `${window.location.origin}/inventory/items/${this.item.id}`;
      }

      const url = await QRCode.toDataURL(data, {
        margin: 0,
        width: 128,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      this.qrCodeUrl.set(url);
    } catch (err) {
      console.error('Error generating QR:', err);
    }
  }

  get compositions(): any[] {
    return this.compositionsList();
  }

  get ia(): string {
    if (!this.item) return 'N/A';
    if (this.item.type === 'TEST_ITEM' || this.item.composition?.length) {
      const comp = this.item.composition?.[0];
      const analiteLabel = comp?.analite?.label || comp?.analite?.name;
      return analiteLabel || this.item.name || 'N/A';
    }
    return this.item.name || 'N/A';
  }

  get nc(): string {
    return this.item?.commercial_name || 'NA';
  }

  get cant(): string {
    if (this.partition) {
      return (this.partition.initial_mass !== undefined && this.partition.initial_mass !== null)
        ? `${this.partition.initial_mass} g`
        : '0.00 g';
    }
    const qty = this.item?.received_quantity !== undefined ? this.item.received_quantity : this.item?.stock;
    if (qty === undefined || qty === null) return '0.00';
    const unitId = this.item?.received_unit_id;
    const unitObj = this.units.find(u => u.value === unitId);
    const unitLabel = unitObj?.label || 'g';
    return `${qty} ${unitLabel}`;
  }

  get prov(): string {
    if (!this.item) return 'N/A';
    const supplierId = this.item.supplierId || this.item.supplier?.id;
    if (supplierId) {
      const sup = this.suppliers.find(s => s.value === supplierId);
      if (sup) return sup.label;
    }
    return this.item.supplier?.name || 'N/A';
  }

  get alm(): string {
    if (!this.item) return 'N/A';
    const locId = this.item.locationId || this.item.location?.id;
    if (locId) {
      const loc = this.locations.find(l => l.id === locId);
      if (loc) return loc.name;
    }
    return this.item.location?.name || 'N/A';
  }

  get lote(): string {
    return this.item?.lot_number || 'N/A';
  }

  get code(): string {
    if (this.partition) {
      return this.partition.code || 'N/A';
    }
    return this.item?.internal_code || 'N/A';
  }

  get spec(): string {
    if (!this.item) return '';
    const comp = this.item.composition?.[0];
    if (comp) {
      const conc = comp.concentration || '0.00';
      const unitObj = this.units.find(u => u.value === comp.unit_id);
      const unitLabel = unitObj?.label || '';
      return `${conc} ${unitLabel} ${this.formulationCode}`.trim();
    }
    return this.formulationCode;
  }

  get formulationCode(): string {
    if (!this.item) return '';
    if (this.item.formulation_type && typeof this.item.formulation_type === 'object') {
      return this.item.formulation_type.code || '';
    }
    return this.item.formulation_type || this.item.formulationType || '';
  }

  get venc(): string {
    if (!this.item?.expiration_date) return 'N/A';
    const date = new Date(this.item.expiration_date);
    if (isNaN(date.getTime())) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  get descarte(): string {
    if (!this.item) return 'N/A';
    const solid = this.item.wasteClassSolid || '';
    const liquid = this.item.wasteClassLiquid || '';
    if (!solid && !liquid) return 'N/A';
    return `${solid}   ${liquid}`.trim();
  }

  get toxicologyRef(): string {
    return this.item?.toxicology_reference || '';
  }

  get sponsor(): string {
    return this.item?.sponsor || '';
  }
}
